// packages/web/src/components/profile-dialog/notifications-section.tsx
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import { meNotificationPrefsOptions } from "@/api/notification-prefs/query-options";
import { useUpsertNotificationPref } from "@/api/notification-prefs/mutations";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";

const TEMPLATE_KEYS = [
  "hours_rejected",
  "hours_edited_by_admin",
  "week_submitted",
  "trip_tagged",
  "trip_amended",
  "trip_withdrawn",
  "lodging_tagged",
  "invoice_missing_reminder",
] as const;
type TemplateKey = (typeof TEMPLATE_KEYS)[number];
type Channel = "email" | "push" | "inapp";

interface PrefCell {
  enabled: boolean;
  isOverride: boolean;
}
type ChannelMap = Record<Channel, PrefCell>;
type Rows = Map<TemplateKey, ChannelMap>;

function isKnownTemplateKey(k: string): k is TemplateKey {
  return (TEMPLATE_KEYS as readonly string[]).includes(k);
}

function reshape(
  items: Array<{
    templateKey: string;
    channel: Channel;
    enabled: boolean;
    isOverride: boolean;
  }>,
): Rows {
  const rows: Rows = new Map();
  for (const k of TEMPLATE_KEYS) {
    rows.set(k, {
      email: { enabled: true, isOverride: false },
      push: { enabled: true, isOverride: false },
      inapp: { enabled: true, isOverride: false },
    });
  }
  for (const item of items) {
    if (!isKnownTemplateKey(item.templateKey)) continue;
    const row = rows.get(item.templateKey);
    if (!row) continue;
    row[item.channel] = { enabled: item.enabled, isOverride: item.isOverride };
  }
  return rows;
}

export function NotificationsSection() {
  const { t } = useTranslation();
  const query = useQuery(meNotificationPrefsOptions());
  const upsert = useUpsertNotificationPref();

  if (query.isPending) {
    return (
      <div className="grid gap-4">
        <div className="grid gap-1">
          <h3 className="text-[15px] font-semibold text-ink-900">
            {t("profile-dialog.notifications.heading")}
          </h3>
          <p className="text-[13px] text-ink-500">
            {t("profile-dialog.notifications.description")}
          </p>
        </div>
        <div className="grid gap-2">
          {TEMPLATE_KEYS.map((k) => (
            <div key={k} className="flex items-center justify-between gap-3">
              <Skeleton className="h-4 w-32" />
              <div className="flex gap-3">
                <Skeleton className="h-4 w-8" />
                <Skeleton className="h-4 w-8" />
                <Skeleton className="h-4 w-8" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (query.isError || !query.data) {
    return (
      <div className="grid gap-3">
        <div className="grid gap-1">
          <h3 className="text-[15px] font-semibold text-ink-900">
            {t("profile-dialog.notifications.heading")}
          </h3>
          <p className="text-[13px] text-vroom-red">
            {t("profile-dialog.notifications.load-error.title")}
          </p>
        </div>
        <div>
          <Button
            type="button"
            variant="default"
            onClick={() => {
              void query.refetch();
            }}
          >
            {t("profile-dialog.notifications.load-error.retry-label")}
          </Button>
        </div>
      </div>
    );
  }

  const handleEmailToggle = (key: TemplateKey, next: boolean) => {
    upsert.mutate(
      { templateKey: key, channel: "email", enabled: next },
      {
        onError: () => {
          toast.error(t("profile-dialog.notifications.toast.error"));
        },
      },
    );
  };

  const rows = reshape(query.data.items);

  return (
    <div className="grid gap-4">
      <div className="grid gap-1">
        <h3 className="text-[15px] font-semibold text-ink-900">
          {t("profile-dialog.notifications.heading")}
        </h3>
        <p className="text-[13px] text-ink-500">{t("profile-dialog.notifications.description")}</p>
      </div>

      {/* Desktop matrix (md+) */}
      <table className="hidden md:table w-full text-left text-[13px]">
        <thead>
          <tr className="text-ink-500">
            <th scope="col" className="font-mono uppercase tracking-[.12em] text-[9.5px] pb-2"></th>
            {(["email", "push", "inapp"] as const).map((ch) => (
              <th
                key={ch}
                scope="col"
                className="font-mono uppercase tracking-[.12em] text-[9.5px] pb-2 w-20 text-center"
              >
                <div className="inline-flex flex-col items-center gap-1">
                  <span>
                    {t(`profile-dialog.notifications.channels.${ch}.label`)}
                    {ch !== "email" && (
                      <span className="sr-only">
                        {" "}
                        ({t("profile-dialog.notifications.channels.binnenkort.label")})
                      </span>
                    )}
                  </span>
                  {ch !== "email" && (
                    <Badge variant="role" aria-hidden="true">
                      {t("profile-dialog.notifications.channels.binnenkort.label")}
                    </Badge>
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {TEMPLATE_KEYS.map((key) => {
            const row = rows.get(key);
            if (!row) return null;
            return (
              <tr key={key} className="hover:bg-surface/40">
                <td className="py-2 text-ink-700">
                  {t(`profile-dialog.notifications.templates.${key}.label`)}
                </td>
                {(["email", "push", "inapp"] as const).map((ch) => {
                  const cell = row[ch];
                  const isEmail = ch === "email";
                  return (
                    <td key={ch} className="py-2 text-center">
                      <Switch
                        checked={cell.enabled}
                        disabled={!isEmail || upsert.isPending}
                        onCheckedChange={
                          isEmail ? (next: boolean) => handleEmailToggle(key, next) : undefined
                        }
                        aria-label={t("profile-dialog.notifications.aria.toggle", {
                          template: t(`profile-dialog.notifications.templates.${key}.label`),
                          channel: t(`profile-dialog.notifications.channels.${ch}.label`),
                        })}
                      />
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Mobile stack (< md) */}
      <div className="grid gap-3 md:hidden">
        {TEMPLATE_KEYS.map((key) => {
          const row = rows.get(key);
          if (!row) return null;
          return (
            <div key={key} className="rounded-3 border border-line bg-surface p-3">
              <div className="mb-2 text-[13px] font-medium text-ink-900">
                {t(`profile-dialog.notifications.templates.${key}.label`)}
              </div>
              <div className="grid gap-2">
                {(["email", "push", "inapp"] as const).map((ch) => {
                  const cell = row[ch];
                  const isEmail = ch === "email";
                  return (
                    <div
                      key={ch}
                      className="flex items-center justify-between text-[13px] text-ink-700"
                    >
                      <span className="inline-flex items-center gap-2">
                        {t(`profile-dialog.notifications.channels.${ch}.label`)}
                        {!isEmail && (
                          <>
                            <span className="sr-only">
                              {" "}
                              ({t("profile-dialog.notifications.channels.binnenkort.label")})
                            </span>
                            <Badge variant="role" aria-hidden="true">
                              {t("profile-dialog.notifications.channels.binnenkort.label")}
                            </Badge>
                          </>
                        )}
                      </span>
                      <Switch
                        checked={cell.enabled}
                        disabled={!isEmail || upsert.isPending}
                        onCheckedChange={
                          isEmail ? (next: boolean) => handleEmailToggle(key, next) : undefined
                        }
                        aria-label={t("profile-dialog.notifications.aria.toggle", {
                          template: t(`profile-dialog.notifications.templates.${key}.label`),
                          channel: t(`profile-dialog.notifications.channels.${ch}.label`),
                        })}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
